import ReactModal from 'react-modal';
import { createRoot } from 'react-dom/client';
import React from 'react';
import { t } from 'javascripts/utils/withTranslation';
import Service from '../react/Service';
import LoginHandler from '../../../../javascripts/globals/login-handler';

function getMinsistriesFromDOM() {
  const $ministries = document.querySelectorAll(
    '.header__ministry .dropdown-menu__link'
  );
  return Array.from($ministries).map(($ministry) => {
    const $text = $ministry.querySelector('.dropdown-menu__text');
    return {
      text: $text?.textContent,
      link: $ministry.href,
      modifier: $ministry.id,
    };
  });
}

function getLanguagesFromDOM() {
  const $languages = document.querySelectorAll(
    '.header__languages .dropdown-menu__link'
  );
  return Array.from($languages).map(($language) => {
    const $text = $language.querySelector('.dropdown-menu__text');
    return {
      text: $text?.textContent,
      link: $language.href,
    };
  });
}

function getExternalsFromDOM() {
  const $externals = document.querySelectorAll('.header__external .icon-link');
  return Array.from($externals).map(($e) => {
    const $text = $e.querySelector('.icon-link__text');
    let visibleText = $text?.textContent;
    if ($text) {
      const $clone = $text.cloneNode(true);
      // Remove accessibility texts as they will get added later again
      $clone
        .querySelectorAll('.u-hidden-visually')
        .forEach((span) => span.remove());
      visibleText = $clone.textContent;
    }
    const $icon = $e.querySelector('.icon');
    const iconClass = Array.from($icon.classList || [])?.find((cls) =>
      cls.startsWith('icon--')
    );
    const iconName = iconClass ? iconClass.replace('icon--', '') : '';
    return {
      text: visibleText,
      icon: iconName,
      link: $e.href,
      external: $e.target === '_blank',
    };
  });
}

/**
 *  We need to alter the json structure that is given by the backend here.
 * This can not be done recursively, as the children on the first level are nested arrays.
 * Look at navigation-sitemap.json for the original structure. The navigation.config.yml used here should have the same contents.
 * */
function transformNavigation(sitemap) {
  // Convert the root object into an array and sort by order
  const rootArray = Object.values(sitemap).sort((a, b) => a.order - b.order);

  // Transform each item's children property
  const transformedRootArray = rootArray.map((item) => {
    if (item.children && Array.isArray(item.children)) {
      // Sort children based on order before transformation
      const sortedChildren = item.children.sort((a, b) => a.order - b.order);
      const transformedChildren = sortedChildren.map((child) => {
        // Convert child object into an array of its values and sort by order
        const childArray = Object.values(child)
          .sort((a, b) => a.order - b.order)
          .map((subChild) => {
            // Check and transform the last level of children
            if (
              subChild.children &&
              typeof subChild.children === 'object' &&
              !Array.isArray(subChild.children)
            ) {
              // Sort the last level of children based on order
              const sortedSubChildren = Object.values(subChild.children).sort(
                (a, b) => a.order - b.order
              );
              return { ...subChild, children: sortedSubChildren };
            }
            return subChild;
          });
        return childArray;
      });
      return { ...item, children: transformedChildren };
    }
    return item;
  });

  return transformedRootArray;
}

export const getItemsFromScript = async ($script) => {
  if (!$script) {
    return [];
  }

  const json = await JSON.parse($script.textContent);

  if (!json || json.length === 0) {
    console.error('No data found in #bwHeaderNavigation');
    return [];
  }

  const transformedNavigation = transformNavigation(json);

  return transformedNavigation;
};

const getCurrentLanguageFromDOM = () => {
  const { lang } = document.documentElement;

  const match = {
    de: t('Deutsch'),
    en: t('English'),
    fr: t('Französisch'),
  };

  return match[lang || 'de'] || lang;
};

const getSuggestionsFromCSL = (searchSuggestItems) => {
  if (!searchSuggestItems) {
    return [];
  }

  return searchSuggestItems.split(',');
};

const toggleMenu = async ($service) => {
  if (!$service) {
    console.error('.header__service not found');
    return;
  }
  const $burger = $service.querySelector('.header__burger .header__icon');
  const $search = $service.querySelector('.header__search .header__icon');
  const $script = document.getElementById('bwHeaderNavigation');
  const searchUrl = $search?.getAttribute('data-url');
  const searchFieldName = $search?.getAttribute('data-field-name');
  const searchSuggest = $search?.getAttribute('data-suggest-url');
  const searchSuggestFieldName = $search?.getAttribute(
    'data-suggest-field-name'
  );
  const searchSuggestItems = $search?.getAttribute('data-suggest');
  const searchTooltip = document.getElementById(
    $search?.getAttribute('data-template')
  )?.innerHTML;
  const burgerTooltip = document.getElementById(
    $burger?.getAttribute('data-template')
  )?.innerHTML;
  const labelId = $service.getAttribute('aria-labelledby');
  const showBurgerDesktop =
    $service?.getAttribute('data-show-burger-desktop') === 'true';
  const isMainNav = $service.closest('.header__main');

  // Login Props
  // bp-classnames are legacy classes that are still used in the bp templates, default should be typo3fe
  const isBp =
    document.body?.classList.contains('v-bp') ||
    window.parent.document.documentElement.classList.contains('v-bp');

  const loginProps = LoginHandler.getLoginPropsFromDom({
    loginSelector: isBp ? '.bp-login' : '.typo3fe-login',
    logoutSelector: isBp ? '.bp-logout' : '.typo3fe-logout',
    userSelector: isBp ? '.bp-profile' : '.typo3fe-user',
  });
  const showLogin = loginProps.loginUrl !== undefined;

  if (!$script) {
    console.error(
      'No script tag #bwHeaderNavigation found (containing the json structure of the navigation)'
    );
    return;
  }

  if (!searchUrl) {
    console.error('No data-url attribute on #header__search found');
  }

  if (searchUrl && !searchFieldName) {
    console.warn(
      'No data-field-name attribute on #header__search found. Using default tx_solr[q] here.'
    );
  }

  if (!searchSuggest) {
    console.error('No data-suggest attribute on #header__search found');
  }

  if (searchSuggest && !searchSuggestFieldName) {
    console.warn(
      'No data-suggest-field-name attribute on #header__search found. Using default tx_solr[queryString] here.'
    );
  }

  if (isMainNav) {
    $service.removeAttribute('aria-labelledby');
  }

  if (showLogin && !loginProps.logoutUrl) {
    console.warn('Logout Button will not be shown, as no logoutUrl is set');
  }

  try {
    const ministries = getMinsistriesFromDOM() || [];
    const languages = getLanguagesFromDOM() || [];
    const externals = getExternalsFromDOM() || [];
    const items = (await getItemsFromScript($script)) || [];
    const currentLanguage = getCurrentLanguageFromDOM() || t('Sprache');
    const suggestionItems = getSuggestionsFromCSL(searchSuggestItems);
    const ministryDropdownName = document.querySelector(
      '.header__ministry .link-dropdown .icon-link__text'
    )?.textContent;

    const root = createRoot($service);
    root.render(
      <Service
        ministries={ministries}
        ministryDropdownName={ministryDropdownName}
        languages={languages}
        externals={externals}
        items={items}
        currentLanguage={currentLanguage}
        suggestUrl={searchSuggest}
        suggestFieldName={searchSuggestFieldName}
        suggestionItems={suggestionItems}
        searchUrl={searchUrl}
        searchFieldName={searchFieldName}
        searchTooltip={searchTooltip}
        burgerTooltip={burgerTooltip}
        labelId={labelId}
        showBurgerDesktop={showBurgerDesktop}
        isMainNav={isMainNav}
        loginProps={showLogin ? loginProps : undefined}
      />
    );
  } catch (e) {
    console.error('Error while initializing Burger Menu and Search', e);
  }
};

document.querySelectorAll('.header__service').forEach(($service) => {
  toggleMenu($service);
});
ReactModal.setAppElement(document.getElementById('page'));

export default toggleMenu;
